import * as XLSX from "xlsx";
import type { EventResultsPayload, TeamResult } from "../api/client";

export type ExportResultsFilenameOpts = {
  /** Prefer display name, e.g. KiDS EXPO 2026 → KiDS_EXPO_2026_Results.xlsx */
  eventName?: string;
  /** Fallback stem if name empty */
  eventSlug?: string;
};

function stemForResultsFile(o: ExportResultsFilenameOpts): string {
  const raw = (
    o.eventName?.trim() ||
    o.eventSlug?.trim() ||
    "KiDS_EXPO_2026"
  ).replace(/\.xlsx$/i, "");
  let s = raw
    .replace(/[^\p{L}\p{N}._-]+/gu, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  if (!s) s = "KiDS_EXPO_2026";
  return s.slice(0, 96);
}

function sheetStem(name: string, maxLen = 28): string {
  let s = name
    .replace(/[[\]()*/\\?:^\]]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "Division";
  if (s.length > maxLen) s = `${s.slice(0, maxLen)}…`;
  return s;
}

const JUDGE_PREFIX: Record<string, string> = {
  judge1: "J1",
  judge2: "J2",
  judge3: "J3",
};

/** Stable ordered set of extra-criteria headers across the teams in a sheet. */
function extraCriteriaHeaders(
  teams: TeamResult[],
): { criterionId: string; header: string }[] {
  const seen = new Map<string, string>();
  for (const team of teams) {
    for (const c of team.extraCriteria) {
      if (!seen.has(c.criterionId)) {
        const prefix = JUDGE_PREFIX[c.judgeRole] ?? c.judgeRole;
        seen.set(c.criterionId, `★ ${prefix} ${c.name} (${c.maxScore})`);
      }
    }
  }
  return [...seen.entries()].map(([criterionId, header]) => ({
    criterionId,
    header,
  }));
}

function rowsForTeams(
  divisionLabel: string,
  teams: TeamResult[],
): Record<string, string | number>[] {
  const extraHeaders = extraCriteriaHeaders(teams);
  return teams.map((r) => {
    const extraByCriterion = new Map(
      r.extraCriteria.map((c) => [c.criterionId, c.value] as const),
    );
    const row: Record<string, string | number> = {
      ອັນດັບຮາງ: r.rank,
      ຮາງ: divisionLabel,
      ລຳດັບ: r.orderNumber,
      ຊື່ທີມ: r.name,
      "J1 Strength & Difficulty": r.judge1.strengthDifficulty,
      "J1 Timing & Control": r.judge1.timingControl,
      "J1 Unity & Synchronizity": r.judge1.unitySynchronicity,
      "J1 Total (30)": r.judge1.total,
      "J2 Charisma & Confidence": r.judge2.charismaConfidence,
      "J2 Facial Expression & Emotions": r.judge2.facialExpression,
      "J2 Stage Awareness & Engagement": r.judge2.stageAwareness,
      "J2 Total (30)": r.judge2.total,
      "J3 Concept & Originality": r.judge3.conceptOriginality,
      "J3 Costume, Props & Visual Composition": r.judge3.costumeVisual,
      "J3 Choreography Creativity & Overall Quality":
        r.judge3.choreographyQuality,
      "J3 Total (40)": r.judge3.total,
    };
    for (const { criterionId, header } of extraHeaders) {
      row[header] = extraByCriterion.get(criterionId) ?? 0;
    }
    row["Extra total"] = r.extraTotal;
    row["Grand total"] = r.grandTotal;
    row["Max total"] = r.maxTotal;
    return row;
  });
}

export function exportResultsToExcel(
  results: EventResultsPayload | TeamResult[],
  legacyFilenameOrOpts?: string | ExportResultsFilenameOpts,
): void {
  let payload: EventResultsPayload;
  if (!Array.isArray(results) && results && "divisions" in results) {
    payload = results;
  } else {
    const flat = Array.isArray(results) ? results : [];
    payload = {
      divisions: [
        {
          divisionId: "_",
          divisionName: "ທັງໝົດ",
          sortOrder: 1,
          roundCount: 1,
          rounds: [{ roundIndex: 1, teams: flat }],
        },
      ],
    };
  }

  let filename: string;
  if (legacyFilenameOrOpts === undefined) {
    filename = `${stemForResultsFile({})}_Results.xlsx`;
  } else if (typeof legacyFilenameOrOpts === "string") {
    filename = legacyFilenameOrOpts.endsWith(".xlsx")
      ? legacyFilenameOrOpts
      : `${legacyFilenameOrOpts}_Results.xlsx`;
  } else {
    filename = `${stemForResultsFile(legacyFilenameOrOpts)}_Results.xlsx`;
  }

  const wb = XLSX.utils.book_new();

  for (const section of payload.divisions) {
    for (const round of section.rounds) {
      if (round.teams.length === 0) continue;
      const divLabel =
        section.roundCount > 1
          ? `${section.divisionName} · ຮອບ ${round.roundIndex}`
          : section.divisionName;
      const rows = rowsForTeams(divLabel, round.teams);
      const ws = XLSX.utils.json_to_sheet(rows);
      if (rows.length > 0) {
        const colWidths = Object.keys(rows[0] ?? {}).map((key) => ({
          wch: Math.max(key.length + 2, 12),
        }));
        ws["!cols"] = colWidths;
      }
      const sheetName = sheetStem(
        section.roundCount > 1
          ? `${section.divisionName} R${round.roundIndex}`
          : section.divisionName,
      );
      let finalName = sheetName;
      let n = 1;
      while (wb.SheetNames.includes(finalName)) {
        finalName = `${sheetName.slice(0, 24)}_${n}`;
        n += 1;
      }
      XLSX.utils.book_append_sheet(wb, ws, finalName);
    }
  }

  if (wb.SheetNames.length === 0) {
    const ws = XLSX.utils.json_to_sheet([]);
    XLSX.utils.book_append_sheet(wb, ws, "ຜົນການແຂ່ງຂັນ");
  }

  XLSX.writeFile(wb, filename);
}
